import { useState } from "react";
import { toast } from "sonner";
import { Share2, Download, Loader2, MessageCircle, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { photoUrl } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const S = 1080;
const monthName = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });

const loadImg = (src) => new Promise((res, rej) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => res(img);
  img.onerror = rej;
  img.src = `${src}${src.includes("?") ? "&" : "?"}share=1`;
});

const cover = (ctx, img, x, y, w, h) => {
  const r = Math.max(w / img.width, h / img.height);
  const sw = w / r, sh = h / r;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
};

const drawCard = async (d) => {
  await document.fonts?.ready;
  const c = document.createElement("canvas");
  c.width = S; c.height = S;
  const ctx = c.getContext("2d");
  ctx.fillStyle = "#0A192F"; ctx.fillRect(0, 0, S, S);
  if (d.photo) {
    try { cover(ctx, await loadImg(photoUrl(d.photo)), 0, 0, S, 700); } catch { /* keep plain background */ }
  }
  const g = ctx.createLinearGradient(0, 380, 0, 760);
  g.addColorStop(0, "rgba(11,17,33,0)"); g.addColorStop(1, "#0A192F");
  ctx.fillStyle = g; ctx.fillRect(0, 380, S, 380);
  // logo
  ctx.fillStyle = "#FF2E63"; ctx.beginPath(); ctx.roundRect(60, 60, 64, 64, 14); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.font = "900 40px Outfit, sans-serif"; ctx.textAlign = "center"; ctx.fillText("✦", 92, 106);
  ctx.textAlign = "left"; ctx.font = "800 40px Outfit, sans-serif";
  ctx.fillStyle = "#FF2E63"; ctx.fillText("Robo", 140, 104);
  ctx.fillStyle = "#fff"; ctx.fillText("Spark", 140 + ctx.measureText("Robo").width, 104);
  // badge
  ctx.save(); ctx.translate(S - 60, 70); ctx.rotate(0.06);
  ctx.fillStyle = "#FF2E63"; ctx.beginPath(); ctx.roundRect(-330, 0, 330, 110, 20); ctx.fill();
  ctx.fillStyle = "#fff"; ctx.textAlign = "right"; ctx.font = "700 22px Outfit, sans-serif"; ctx.fillText("MAKER OF THE MONTH", -28, 42);
  ctx.font = "900 36px Outfit, sans-serif"; ctx.fillText(monthName(d.month), -28, 86); ctx.restore();
  // text
  ctx.textAlign = "left"; ctx.fillStyle = "#00E5FF"; ctx.font = "700 28px Outfit, sans-serif";
  ctx.fillText("🏆  CHAMPION · मेकर ऑफ़ द मंथ", 70, 790);
  ctx.fillStyle = "#fff"; ctx.font = "900 120px Outfit, sans-serif"; ctx.fillText(d.first_name, 66, 910);
  ctx.fillStyle = "rgba(255,255,255,0.8)"; ctx.font = "500 36px Outfit, sans-serif";
  ctx.fillText(`${d.school_name}${d.program ? ` · ${d.program}` : ""}`.slice(0, 52), 70, 970);
  ctx.fillStyle = "#FF2E63"; ctx.fillRect(70, 1010, 120, 8);
  ctx.fillStyle = "rgba(255,255,255,0.55)"; ctx.font = "500 24px Outfit, sans-serif"; ctx.textAlign = "right";
  ctx.fillText("Learn · Build · Earn  —  robospark", S - 70, 1022);
  return new Promise((res) => c.toBlob(res, "image/png"));
};

// Generic share flow: native share sheet with the PNG when supported, else a preview dialog with Save / WhatsApp / Instagram.
export const ShareImageButton = ({ makeBlob, fname, text, title, variant = "dark", testid, label }) => {
  const { L } = useLang();
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const share = async () => {
    setBusy(true);
    try {
      const blob = await makeBlob();
      const file = new File([blob], fname, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        try { await navigator.share({ files: [file], text }); return; } catch (e) { if (e.name === "AbortError") return; }
      }
      setPreview({ url: URL.createObjectURL(blob) });
    } catch { toast.error(L("Could not create the image.", "इमेज नहीं बन सकी।")); } finally { setBusy(false); }
  };
  const close = () => { URL.revokeObjectURL(preview.url); setPreview(null); };
  const download = () => { const a = document.createElement("a"); a.href = preview.url; a.download = fname; a.click(); };

  return (
    <>
      <Button onClick={share} disabled={busy} data-testid={testid}
        className={`rounded-full ${variant === "dark" ? "bg-[#0A192F] hover:bg-[#13294B] text-white" : "bg-[#FF3B3B] hover:bg-[#E63946] text-white"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-1" />{label || L("Share", "शेयर करें")}</>}
      </Button>
      <Dialog open={!!preview} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-md" data-testid="share-card-dialog">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{L("Download the image, then post it on WhatsApp or Instagram.", "इमेज डाउनलोड करें, फिर WhatsApp या Instagram पर पोस्ट करें।")}</DialogDescription>
          </DialogHeader>
          {preview && <img src={preview.url} alt="Share card" className="w-full rounded-xl border" data-testid="share-card-preview" />}
          <div className="grid grid-cols-3 gap-2">
            <Button onClick={download} variant="outline" data-testid="share-card-download"><Download className="w-4 h-4 mr-1" />{L("Save", "सेव")}</Button>
            <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noreferrer" onClick={download} data-testid="share-card-whatsapp"
              className="inline-flex items-center justify-center rounded-md bg-[#25D366] text-white text-sm font-semibold h-10"><MessageCircle className="w-4 h-4 mr-1" />WhatsApp</a>
            <a href="https://www.instagram.com/" target="_blank" rel="noreferrer" onClick={download} data-testid="share-card-instagram"
              className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-[#f58529] via-[#dd2a7b] to-[#8134af] text-white text-sm font-semibold h-10"><Instagram className="w-4 h-4 mr-1" />Instagram</a>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ShareChampionButton = ({ data, variant = "dark", testid = "share-champion-btn" }) => {
  const { L } = useLang();
  const text = L(`${data.first_name} from ${data.school_name} is RoboSpark's Maker of the Month (${monthName(data.month)})! ${window.location.origin}`,
    `${data.school_name} के ${data.first_name} RoboSpark के मेकर ऑफ़ द मंथ (${monthName(data.month)}) हैं! ${window.location.origin}`);
  return <ShareImageButton makeBlob={() => drawCard(data)} fname={`RoboSpark-Maker-of-the-Month-${data.month}-${data.first_name}.png`}
    text={text} title={L("Share the champion", "चैंपियन को शेयर करें")} variant={variant} testid={testid} />;
};
